import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Divider } from '@abyss/web/ui/Divider';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Provider } from '../../models/Provider';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { mobileOnly, notXlScreenOnly, tabletScreen } from '../ConstantsStyles';
import {
  BottomWrapper,
  BottomWrapperChoosePCP,
  BottomWrapperChoosePCPGroupedLocation,
  ChooseAsPCPButton,
  GroupedPinBottomWrapper,
  ResponsiveText,
  contactContainerStyles,
} from './DataCard.style';
import { Direction } from './Direction';
import { Phone } from './Phone';
import { Schedule, scheduleTextStyles } from './Schedule';
import { ScheduleWithFacility } from './ScheduleWithFacility';

const getPCPButtonLabelKey = (
  isNavigatingMaestro: boolean,
  isUpdateProvider?: boolean
): string => {
  if (!isNavigatingMaestro) return 'CHOOSE_PCP';
  return isUpdateProvider ? 'UPDATE_THIS_PROVIDER' : 'SELECT_THIS_PROVIDER';
};

const getScheduleContent = (
  isCTADisplayLogic: boolean,
  doShowDocASAP: boolean | string,
  isLimitedAccessToProvider: boolean | undefined,
  showScheduleWithFacilityButton: React.ReactNode,
  showScheduleButton: React.ReactNode,
  scheduleButton: React.ReactNode
): React.ReactNode => {
  if (!isCTADisplayLogic) return scheduleButton;
  return doShowDocASAP && isLimitedAccessToProvider
    ? showScheduleWithFacilityButton
    : showScheduleButton;
};

const groupedPinChoosePcp = (
  callButton,
  t,
  name,
  providerId,
  sectionTypeDerived,
  handleChoosePCPClick
) => (
  <BottomWrapperChoosePCPGroupedLocation justify="flex-end">
    {callButton}
    <ChooseAsPCPButton
      aria-label={`${t('PRIMARY_CARE_PROVIDER.CHOOSE_PCP')} ${name}`}
      css={{ fontSize: '16px', fontWeight: '700' }}
      data-auto-testid="choose-pcp-button"
      data-providerid={providerId}
      data-testid={`${'choose-pcp-button'}-${sectionTypeDerived.replace(
        / /g,
        '-'
      )}`}
      onClick={handleChoosePCPClick}
      size="$sm"
      variant="outline"
    >
      {t('PRIMARY_CARE_PROVIDER.CHOOSE_PCP')}
    </ChooseAsPCPButton>
  </BottomWrapperChoosePCPGroupedLocation>
);

const groupedPinCallAndSceduleDisplay = (
  smallScreen,
  isCTADisplayLogic,
  handleAdobeSearchResultClickEvent,
  handleNavigationOnClick,
  sectionTypeDerived,
  callButton,
  doShowDocASAP,
  isLimitedAccessToProvider,
  showScheduleWithFacilityButton,
  showScheduleButton,
  scheduleButton
) => (
  <GroupedPinBottomWrapper
    css={{
      display: 'flex',
      alignItems: 'center',
      border: '1px solid transparent !important',
      paddingRight: '',
      gap: '8px',
      width: smallScreen && '330px',
      paddingTop: smallScreen && '0px',
    }}
    justify={'flex-end'}
  >
    {!isCTADisplayLogic ? (
      <>
        <Direction
          handleClick={() => {
            handleAdobeSearchResultClickEvent('address');
            handleNavigationOnClick();
          }}
          sectionType={sectionTypeDerived}
        />
        <div style={{ height: 'auto' }}>
          <Divider margin={0} orientation="vertical" width={1} />
        </div>{' '}
      </>
    ) : null}
    {callButton}
    {getScheduleContent(
      isCTADisplayLogic,
      doShowDocASAP,
      isLimitedAccessToProvider,
      showScheduleWithFacilityButton,
      showScheduleButton,
      scheduleButton
    )}
  </GroupedPinBottomWrapper>
);

type Props = {
  choosePCP: boolean;
  providerId: string;
  sectionTypeDerived: string;
  handleChoosePCPClick: any;
  headers: ResponseHeaders;
  getLinkPositionIndex: any;
  linkLocationName: string;
  index?: number;
  name: string;
  primaryPhone: string;
  practitioner?: Provider;
  providerType?: string;
  searchTerm?: string;
  handleAdobeSearchResultClickEvent: any;
  selectedFilters?: string;
  handleNavigationOnClick: any;
  npi;
  docasapPracticeId: string;
  locationId: string;
  doShowDocASAP?: boolean | string;
  isCTADisplayLogic?: boolean;
  showDirection: any;
  isTieredDataCard: boolean;
  isLimitedAccessToProvider?: boolean;
  isSchedulingWithFacilityEnabled?: boolean;
  enableMapViewHideOption?: boolean;
  isGroupedLocationDrawer?: boolean;
  expandedProviderListView?: boolean;
  isUmr?: boolean;
  isSchedulingDataIndicator?: boolean;
  isNewMapViewDataCardDesign?: boolean;
  isNavigatingMaestro: boolean;
  isUpdateProvider?: boolean;
};
export const ChoosePCP = ({
  selectedFilters,
  choosePCP,
  providerId,
  locationId,
  sectionTypeDerived,
  handleChoosePCPClick,
  headers,
  getLinkPositionIndex,
  linkLocationName,
  index,
  name,
  primaryPhone,
  practitioner,
  providerType,
  searchTerm,
  handleAdobeSearchResultClickEvent,
  handleNavigationOnClick,
  npi,
  docasapPracticeId,
  doShowDocASAP = false,
  isCTADisplayLogic = false,
  showDirection,
  isTieredDataCard,
  isLimitedAccessToProvider,
  isSchedulingWithFacilityEnabled = false,
  enableMapViewHideOption,
  isGroupedLocationDrawer,
  expandedProviderListView,
  isUmr,
  isSchedulingDataIndicator,
  isNewMapViewDataCardDesign,
  isNavigatingMaestro,
  isUpdateProvider,
}: Props) => {
  const smallScreen = useMediaQuery(mobileOnly);
  const tabletScreenView = useMediaQuery(tabletScreen);
  const notXlScreen = useMediaQuery(notXlScreenOnly);
  const callToScheduleSize =
    isGroupedLocationDrawer && notXlScreen ? '$sm' : '$md';
  const { t } = useTranslation();

  const isSelectProvider = choosePCP || isNavigatingMaestro;
  const pcpLabelKey = `PRIMARY_CARE_PROVIDER.${getPCPButtonLabelKey(
    isNavigatingMaestro,
    isUpdateProvider
  )}`;
  const scheduleButton = React.useMemo(
    () => (
      <Schedule
        enableMapViewHideOption={enableMapViewHideOption}
        expandedProviderListView={expandedProviderListView}
        handleSearchResultClick={handleAdobeSearchResultClickEvent}
        id={providerId}
        isAscensionIndicator={isSchedulingDataIndicator}
        isCTADisplayLogic={isCTADisplayLogic}
        isGroupedLocationDrawer={isGroupedLocationDrawer}
        isTieredDataCard={isTieredDataCard}
        locationId={locationId}
        npi={npi}
        phone={primaryPhone}
        practiceId={docasapPracticeId}
        practitioner={practitioner}
        providerName={name}
        sectionType={sectionTypeDerived}
        showDirection={showDirection}
      />
    ),
    []
  );

  const callButton = React.useMemo(
    () => (
      <Phone
        callToScheduleSize={callToScheduleSize}
        choosePCP={isSelectProvider}
        doShowDocASAP={doShowDocASAP}
        enableMapViewHideOption={enableMapViewHideOption}
        expandedProviderListView={expandedProviderListView}
        headers={headers}
        id={providerId}
        indexForAnalytics={getLinkPositionIndex(index)}
        isCTADisplayLogic={isCTADisplayLogic}
        isGroupedLocationDrawer={isGroupedLocationDrawer}
        isSchedulingDataIndicator={isSchedulingDataIndicator}
        locationForAnalytics={linkLocationName}
        phone={primaryPhone}
        practitioner={practitioner}
        providerType={providerType}
        searchTerm={searchTerm}
        sectionType={sectionTypeDerived}
        selectedFilters={selectedFilters}
      />
    ),
    [callToScheduleSize]
  );

  const scheduleText = (
    <ResponsiveText
      color="$interactive1"
      css={scheduleTextStyles}
      data-auto-testid={`data-card-schedule-with-facility-${sectionTypeDerived}-${providerId}`}
      data-testid={`data-card-schedule-with-facility-${sectionTypeDerived}-${providerId}`}
      fontWeight="$bold"
    >
      {t('DATA_CARD.SCHEDULE_WITH_FACILITY')}
    </ResponsiveText>
  );

  const scheduleButtonWithFacility = React.useMemo(
    () => (
      <>
        <div style={{ height: 'auto' }}>
          <Divider margin={0} orientation="vertical" width={1} />
        </div>
        <ScheduleWithFacility
          primaryPhone={primaryPhone}
          providerId={providerId}
          scheduleText={scheduleText}
        />
      </>
    ),
    []
  );

  const showScheduleButton =
    doShowDocASAP || isSchedulingDataIndicator ? scheduleButton : null;
  const showScheduleWithFacilityButton = isSchedulingWithFacilityEnabled
    ? scheduleButtonWithFacility
    : null;

  return (
    <React.Fragment>
      {isSelectProvider ? (
        isGroupedLocationDrawer ? (
          groupedPinChoosePcp(
            callButton,
            t,
            name,
            providerId,
            sectionTypeDerived,
            handleChoosePCPClick
          )
        ) : (
          <BottomWrapperChoosePCP
            alignItems="center"
            css={{
              ...(!enableMapViewHideOption && {
                borderTop: '1px solid transparent !important',
              }),
            }}
            cssProps={{
              isNavigatingMaestro,
              isNewMapViewDataCardDesign,
            }}
            justify="space-between"
          >
            <ChooseAsPCPButton
              aria-label={`${t(pcpLabelKey)} ${name}`}
              data-auto-testid="choose-pcp-button"
              data-providerid={providerId}
              data-testid={`${'choose-pcp-button'}-${sectionTypeDerived.replace(
                / /g,
                '-'
              )}`}
              onClick={handleChoosePCPClick}
              size="$sm"
              variant="outline"
            >
              {t(pcpLabelKey)}
            </ChooseAsPCPButton>
            {callButton}
          </BottomWrapperChoosePCP>
        )
      ) : isGroupedLocationDrawer ? (
        groupedPinCallAndSceduleDisplay(
          smallScreen,
          isCTADisplayLogic,
          handleAdobeSearchResultClickEvent,
          handleNavigationOnClick,
          sectionTypeDerived,
          callButton,
          doShowDocASAP,
          isLimitedAccessToProvider,
          showScheduleWithFacilityButton,
          showScheduleButton,
          scheduleButton
        )
      ) : (
        <BottomWrapper
          css={{
            ...(enableMapViewHideOption &&
              contactContainerStyles(
                expandedProviderListView,
                tabletScreenView
              )),
            '@screen < $xl': {
              flexDirection: expandedProviderListView && 'column',
            },
          }}
          cssProps={{
            enableMapViewHideOption,
            expandedProviderListView,
          }}
          justify={enableMapViewHideOption ? 'flex-end' : 'space-evenly'}
        >
          {!isCTADisplayLogic ? (
            <>
              <Direction
                handleClick={() => {
                  handleAdobeSearchResultClickEvent('address');
                  handleNavigationOnClick();
                }}
                sectionType={sectionTypeDerived}
              />
              <div style={{ height: 'auto' }}>
                <Divider margin={0} orientation="vertical" width={1} />
              </div>{' '}
            </>
          ) : null}
          {callButton}
          {!isUmr &&
            getScheduleContent(
              isCTADisplayLogic,
              doShowDocASAP,
              isLimitedAccessToProvider,
              showScheduleWithFacilityButton,
              showScheduleButton,
              scheduleButton
            )}
        </BottomWrapper>
      )}
    </React.Fragment>
  );
};
